// Browser auth helpers: one shared Supabase client, session + profile, logout,
// and password reset. Bundled by cloudflare/build.mjs (esbuild) into dist/js/.

import { createClient, type AuthChangeEvent, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { sessionStorageAdapter, sharedCookieDomain } from './shared-storage';
import { PROFILE_COLUMNS, type Profile, type ProfileUpdate } from './types';

// Injected at build time by cloudflare/build.mjs (esbuild `define`) from
// wrangler.toml [vars], or from SUPABASE_URL / SUPABASE_ANON_KEY in the build
// environment. This is the equivalent of NEXT_PUBLIC_* / VITE_* variables:
// browsers have no process.env, so the public values are baked in at build time.
declare const __SUPABASE_URL__: string;
declare const __SUPABASE_ANON_KEY__: string;

export const MIN_PASSWORD_LENGTH = 12;

/**
 * Where email links (confirm, reset, email change) land. It uses the current
 * origin, so afterhourz.tv, localhost:3000 and 127.0.0.1:8787 each get their
 * own callback; all of them are on the Supabase redirect allowlist.
 */
export function authCallbackUrl(next?: string): string {
  // Subdomains (e.g. studio.afterhourz.tv) share the session cookie, so their
  // email links go through the main site's callback, which is on the allowlist.
  const base = sharedCookieDomain() ? `${location.protocol}//afterhourz.tv${location.port ? `:${location.port}` : ''}` : location.origin;
  const url = new URL('/auth/callback', base);
  if (next) url.searchParams.set('next', next);
  return url.toString();
}

// http:// is allowed for a local `supabase start` stack.
const isConfigured = /^https?:\/\/.+/.test(__SUPABASE_URL__) && !__SUPABASE_URL__.includes('YOUR-PROJECT') &&
  Boolean(__SUPABASE_ANON_KEY__) && !__SUPABASE_ANON_KEY__.startsWith('YOUR-');

let client: SupabaseClient | null | undefined;

/** The shared client, or null when Supabase isn't configured (static preview). */
export function getSupabase(): SupabaseClient | null {
  if (client !== undefined) return client;
  client = isConfigured
    ? createClient(__SUPABASE_URL__, __SUPABASE_ANON_KEY__, {
        auth: {
          persistSession: true,
          // Shared cookies on *.afterhourz.tv so afterhourz.tv and studio.afterhourz.tv
          // share one sign-in; localStorage elsewhere. See shared-storage.ts.
          storage: sessionStorageAdapter(),
          autoRefreshToken: true,
          detectSessionInUrl: true, // picks up tokens from email links (confirm, recovery)
          // Implicit flow, so reset links work when opened on another device.
          // PKCE requires the link to open in the browser that asked for it.
          flowType: 'implicit',
        },
      })
    : null;
  return client;
}

function requireSupabase(): SupabaseClient {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Accounts are not available yet.');
  return supabase;
}

// ---------------------------------------------------------------------------
// Session & profile
// ---------------------------------------------------------------------------

export async function getSession(): Promise<Session | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await requireSupabase()
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .eq('id', userId)
    .maybeSingle<Profile>();
  if (error) throw error;
  return data;
}

/**
 * Sign in with an email address or a username. An email goes straight to Supabase. A username is
 * turned into its email by the Worker (POST /api/auth/login), which never reveals it: only the
 * session comes back. Used by /login and by the app's sign-in popup (window.AHAuth).
 * Resolves like supabase.auth.signInWithPassword; errors carry `code` and `status`.
 */
export async function signInWithIdentifier(identifier: string, password: string): Promise<{ data: { session: Session | null; user: Session['user'] | null }; error: (Error & { code?: string; status?: number }) | null }> {
  const supabase = requireSupabase();
  const id = identifier.trim();
  if (id.includes('@')) {
    const { data, error } = await supabase.auth.signInWithPassword({ email: id.toLowerCase(), password });
    return { data: { session: data.session, user: data.user }, error };
  }
  const fail = (message: string, code?: string, status?: number) =>
    ({ data: { session: null, user: null }, error: Object.assign(new Error(message), { code, status }) });
  let res: Response;
  try {
    res = await fetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identifier: id, password }) });
  } catch {
    return fail('We couldn’t reach the server. Check your connection and try again.');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.session) return fail(body.error ?? 'We couldn’t sign you in. Please try again.', body.code, res.status);
  const { data, error } = await supabase.auth.setSession(body.session);
  return { data: { session: data.session, user: data.user }, error };
}

/** The signed-in session and its profile, or nulls when signed out. */
export async function getSessionWithProfile(): Promise<{ session: Session | null; profile: Profile | null }> {
  const session = await getSession();
  if (!session) return { session: null, profile: null };
  return { session, profile: await getProfile(session.user.id) };
}

export async function updateMyProfile(changes: ProfileUpdate): Promise<Profile> {
  const session = await getSession();
  if (!session) throw new Error('You need to sign in first.');
  const { data, error } = await requireSupabase()
    .from('profiles')
    .update(changes)
    .eq('id', session.user.id)
    .select(PROFILE_COLUMNS)
    .single<Profile>();
  if (error) throw error;
  return data;
}

/**
 * Calls back with the session and profile now and on every auth change
 * (sign-in, sign-out, token refresh, password recovery). Returns an unsubscribe function.
 */
export function onAuthChange(
  callback: (state: { event: AuthChangeEvent | 'INITIAL'; session: Session | null; profile: Profile | null }) => void,
): () => void {
  const supabase = getSupabase();
  if (!supabase) {
    callback({ event: 'INITIAL', session: null, profile: null });
    return () => {};
  }
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    // Don't call Supabase inside this callback: it holds the auth lock. Defer instead.
    setTimeout(async () => {
      let profile: Profile | null = null;
      if (session) {
        try {
          profile = await getProfile(session.user.id);
        } catch (err) {
          console.error('Failed to load profile', err);
        }
      }
      callback({ event, session, profile });
    }, 0);
  });
  return () => data.subscription.unsubscribe();
}

/** Signs out on this device and clears the stored session. */
export async function signOut(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

export type ReportReason = 'underage_suspicion' | 'non_consensual' | 'harassment' | 'copyright_infringement' | 'spam' | 'other';

/**
 * Files a report about a user and/or video. Reporters can't read reports back
 * (RLS keeps them staff-only), so this inserts without asking for the row.
 * Using .insert().select() would fail with an RLS error.
 */
export async function fileReport(report: { reportedUserId?: string; videoId?: string; reason: ReportReason; details?: string }) {
  const session = await getSession();
  if (!session) throw new Error('You need to sign in to report.');
  const { error } = await requireSupabase().from('user_reports').insert({
    reporter_id: session.user.id,
    reported_user_id: report.reportedUserId ?? null,
    video_id: report.videoId ?? null,
    reason: report.reason,
    details: report.details?.slice(0, 5000) ?? null,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

/**
 * Sends a password reset email. It resolves the same way whether or not the
 * address has an account, so the reply can't be used to discover users.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  // The link goes through /auth/callback, which sends recovery sessions on to /reset-password.
  const { error } = await requireSupabase().auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo: authCallbackUrl(),
  });
  // Only surface rate limiting; other errors would reveal whether the account exists.
  if (error?.status === 429) throw new Error('Too many requests. Please wait a minute and try again.');
  if (error) console.error('resetPasswordForEmail failed', error);
}

/** Re-sends the signup confirmation email (for expired or lost links). Neutral like the reset request. */
export async function resendConfirmation(email: string): Promise<void> {
  const { error } = await requireSupabase().auth.resend({
    type: 'signup',
    email: email.trim().toLowerCase(),
    options: { emailRedirectTo: authCallbackUrl() },
  });
  if (error?.status === 429) throw new Error('Too many requests. Please wait a minute and try again.');
  if (error) console.error('resend confirmation failed', error);
}

/** Sets a new password. Needs the recovery session from the reset email link. */
export async function updatePassword(password: string): Promise<void> {
  if (password.length < MIN_PASSWORD_LENGTH) {
    throw new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
  }
  const supabase = requireSupabase();
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error('Your reset link has expired. Please request a new one.');
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}
